import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {Policy} from 'auth/PolicyConstants';
import {navigate} from 'BenchlingUtil';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARK, RED_LIGHT} from 'ui-library/Colors';
import AddIcon from 'ui-library/icons/AddIcon';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import ReorderableSimpleTable from 'ui-library/reorderable/ReorderableSimpleTable';
import runModal from 'ui-library/runModal';
import * as modals from 'util/Modals';
import {BasicEvent} from 'util/Types';

import ClonePolicyModal from './ClonePolicyModal';

const styles = StyleSheet.create({
  actionRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ':hover .deleteIcon': {
      display: 'block',
    },
  },
  deleteIcon: {
    display: 'none',
    color: GRAY_DARK,
    ':hover': {
      color: RED_LIGHT,
      pointer: 'cursor',
    },
  },
});

interface Props<T> {
  policies: Array<T>;
  getPolicyURL: (policyId: string) => string;
  deletePolicy: (policyId: string) => Promise<void>;
  clonePolicy: (policyId: string, name: string) => Promise<void>;
  reorderPolicies: (policyIds: Array<string>) => Promise<void>;
}

type ListablePolicy = Pick<Policy, 'id' | 'name' | 'permissions'>;

export default class PoliciesListing<T extends ListablePolicy> extends React.Component<Props<T>> {
  _handleOpenPolicy = (e: BasicEvent, policyId: string): void => {
    navigate(e, this.props.getPolicyURL(policyId));
  };

  _handleDeletePolicy = async (policy: T): Promise<void> => {
    const confirm = await Promise.resolve(
      modals.confirm(
        'Delete access policy',
        `Are you sure you want to delete ${policy.name}?`,
        ButtonStyle.NEGATIVE,
        {
          promiseCompatible: true,
          enableRedesign: true,
        }
      )
    );
    if (!confirm) {
      return;
    }
    this.props.deletePolicy(policy.id);
  };

  _handleClonePolicy = async (): Promise<void> => {
    const clonePolicyParams = await runModal(ClonePolicyModal, {
      policies: this.props.policies,
    });
    if (!clonePolicyParams) {
      return;
    }
    const {policyId, name} = clonePolicyParams;
    this.props.clonePolicy(policyId, name);
  };

  _handleReorderPolicies = (policyId: string, newIndex: number): void => {
    const oldIndex = this.props.policies.findIndex((policy) => policy.id === policyId);
    const reorderedPolicies = [...this.props.policies];
    const [reorderedPolicy] = reorderedPolicies.splice(oldIndex, 1);
    reorderedPolicies.splice(newIndex, 0, reorderedPolicy);
    const reorderedPolicyIds = reorderedPolicies.map((policy) => policy.id);
    this.props.reorderPolicies(reorderedPolicyIds);
  };

  render(): JSX.Element {
    return (
      <ReorderableSimpleTable
        headerCells={[
          {
            key: 'name',
            contents: (
              <div>
                Name
                {/* Ideally the following would be gated on whether the user is a tenant admin, but backend
                currently has extra checks, so for now we just gate on all policies being writable. */}
                {this.props.policies.length > 0 &&
                  this.props.policies.every((policy: T) => policy.permissions.writable) && (
                    <AddIcon onClick={this._handleClonePolicy} />
                  )}
              </div>
            ),
          },
        ]}
        isDraggingDisabled={this.props.policies.some((policy) => !policy.permissions.writable)}
        onReorderRow={this._handleReorderPolicies}
        noRowsDisplay="No policies"
        rows={this.props.policies.map((policy: T) => ({
          id: policy.id,
          cells: [
            {
              key: policy.id,
              contents: (
                <div className={css(styles.actionRow)}>
                  <a
                    href={this.props.getPolicyURL(policy.id)}
                    onClick={(e) => {
                      this._handleOpenPolicy(e, policy.id);
                    }}
                  >
                    {policy.name}
                  </a>
                  {policy.permissions.writable && (
                    <div
                      className={classNames('deleteIcon', css(styles.deleteIcon))}
                      onClick={() => {
                        this._handleDeletePolicy(policy);
                      }}
                    >
                      <BenchlingIcon icon="format-delete" />
                    </div>
                  )}
                </div>
              ),
            },
          ],
        }))}
      />
    );
  }
}
